import File from "../../../../../lib/html5-software-library/src/ranj/eng/content/File";
import FileModelEvent from "../event/FileModelEvent";
import FileTree from "./FileTree";
import FileTreeNode from "./FileTreeNode";
import Directory from "./Directory";

export default class FileModel {
    /**
     * @constructor
     * @param {soma.EventDispatcher} dispatcher 
     */
    constructor(dispatcher) {
        this._dispatcher = dispatcher;

        /** @type {Array<File>} */
        this._newFiles = [];
        /** @type {Array<File>} */
        this._openedFiles = [];

        this._fileTree = new FileTree();
    }

    get newFiles() {
        return this._newFiles;
    }

    get openedFiles() {
        return this._openedFiles;
    }

    get files() {
        return [...this.newFiles, ...this.openedFiles];
    }

    get newFileCount() {
        return this._newFiles.length;
    }

    get openedFilesCount() {
        return this._openedFiles.length;
    }

    get filesCount() {
        return this.newFileCount + this.openedFilesCount;
    }

    get fileTree() {
        return this._fileTree;
    }

    /**
     * Adds a file to the model
     * @param {File} file 
     */
    addFile(file) {
        this._newFiles.push(file);

        const path = file.getPath();
        const parentNode = this._fileTree.getNodeAtPath(this._splitPath(path), true);
        const fileNode = new FileTreeNode();
        fileNode.data = file;
        parentNode.addChild(fileNode);

        this._dispatcher.dispatch(FileModelEvent.FILE_ADDED, file);
    }

    /**
     * Removes a file and dispatches a FILE_REMOVED event
     * @param {File} file
     */
    removeFile(file) {
        if (this._newFiles.remove(file) || this._openedFiles.remove(file)) {
            const path = file.getPath();
            const parentNode = this._fileTree.getNodeAtPath(this._splitPath(path));
            const fileNode = parentNode.children.find(c => c.data === file);
            parentNode.removeChild(fileNode);

            this._dispatcher.dispatch(FileModelEvent.FILE_REMOVED, file);
            return;
        }

        console.error(`Tried removing a file that was not known to the FileModel.`, file);
    }

    /**
     * Marks a file as opened
     * @param {File} file 
     */
    markFileAsOpened(file) {
        if (this._newFiles.remove(file)) {
            this._openedFiles.push(file);
            this._dispatcher.dispatch(FileModelEvent.FILE_SEEN, file);
        }
    }

    /**
     * Checks if a file is a new file
     * @param {File} file 
     * @returns {Boolean} Whether the file is new or not
     */
    fileIsNew(file) {
        return this._newFiles.indexOf(file) > -1;
    }

    /**
     * Checks if a branch contains new files
     * @param {FileTreeNode} fileTreeNode 
     * @returns {Boolean}
     */
    branchContainsNewFiles(fileTreeNode) {
        for (let i = 0; i < fileTreeNode.children.length; i++) {
            const childNode = fileTreeNode.children[i];
            const childNodeData = childNode.data;
            if (childNodeData instanceof File) {
                const file = childNodeData;
                if (this.fileIsNew(file)) {
                    return true;
                }
            }

            // Recursively look through underlying directories
            if (this.branchContainsNewFiles(childNode)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Breaks a path up in an array of strings, split on the '/' character.
     * @param {String} path 
     * @returns {Array<String>}
     */
    _splitPath(pathString) {
        return pathString.split('/');
    }
}